/** Shared multiplayer WebSocket protocol types. */

export type MpPhase =
  | "lobby"
  | "speedrun_level_select"
  | "sandbox_config"
  | "submode_select"
  | "soccer_team_select"
  | "playing"
  | "results"
  | "speedrun_results";

export type MpSpeedrunResultEntry = {
  id: string;
  name: string;
  timeMs: number;
  device: "pc" | "mobile";
  rank: number;
};

export type MpGameMode = "normal" | "speedrun" | "wasd" | "sandbox" | "highway" | "snake" | "soccer";

export type SoccerTeamId = "blue" | "orange";
export type SoccerFormat = "1v1" | "2v2";
export type SoccerVariantId = "normal" | "mega";
export type SoccerOrigin = "compete" | "multiplayer" | "local";

export type SoccerPlayerWire = {
  id: string;
  name: string;
  team: SoccerTeamId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  connected: boolean;
  goals: number;
  shots: number;
  saves: number;
  touches: number;
  posts: number;
  boostCharge?: number;
  boostActive?: boolean;
  equippedCosmeticIds?: string[];
  dots?: number;
  tournamentsWon?: number;
  soccerWins?: number;
  cursorColor?: string;
  accountId?: string;
};

export type SoccerPickupWire = {
  id: string;
  kind: "max" | "small";
  x: number;
  y: number;
  respawnAt: number | null;
};

export type SoccerStateWire = {
  phase: string;
  variant?: SoccerVariantId;
  ball: { x: number; y: number; vx: number; vy: number; spin?: number; angle?: number };
  players: SoccerPlayerWire[];
  pickups?: SoccerPickupWire[];
  scoreBlue: number;
  scoreOrange: number;
  timeLeft: number;
  overtimeTime: number;
  countdown: number;
  goVisible: boolean;
  showFiveMinutes: boolean;
  fiveMinutesAtTop: boolean;
  lastGoalTeam: SoccerTeamId | null;
  lastGoalId: number;
  messages: Array<{ kind: string; text: string; until: number }>;
  timeBanner: { text: string; until: number } | null;
  replay: {
    frames: Array<{
      t: number;
      ball: { x: number; y: number; vx: number; vy: number; spin?: number; angle?: number };
      players: Array<{ id: string; x: number; y: number; team: SoccerTeamId }>;
      highlight: boolean;
    }>;
    duration: number;
  } | null;
  replayT: number;
  replayAwaitingNames?: string[];
  winnerTeam: SoccerTeamId | null;
  forfeitReason: string | null;
  afkWarned: string[];
  paused: boolean;
  allowTeamChange: boolean;
  compete: boolean;
  format: SoccerFormat;
};

export type SoccerProfileWire = {
  dots?: number;
  tournamentsWon?: number;
  soccerWins?: number;
  cursorColor?: string;
  accountId?: string;
};

export type MpSubMode = "ffa" | "coop";

export type MpSandboxConfigWire = {
  schemaVersion?: 2;
  baseMode?: string;
  ballsSize: number;
  ballsSpeed: number;
  initialBalls?: number;
  ballsPerSpawn: number;
  pointsPerSpawn: number;
  bounceSpeedBoost: number;
  cursorSpeed: number;
  cursorSize: number;
  smoothCursor: number;
  ballCollisionsEnabled: boolean;
  wasdEnabled: boolean;
  progressionEnabled: boolean;
  magnetEnabled: boolean;
  mode?: Record<string, number | boolean | string>;
};

export type MpPlayer = {
  id: string;
  name: string;
  isHost: boolean;
  isSpectator: boolean;
  alive: boolean;
  joinedAt: number;
  /** Shop cosmetic ids equipped by this player (resolved client-side from catalog). */
  equippedCosmeticIds?: string[];
};

export type MpRoomSnapshot = {
  code: string;
  phase: MpPhase;
  hostId: string;
  players: MpPlayer[];
  selectedMode: MpGameMode | null;
  subMode: MpSubMode | null;
  speedrunLevel: number | null;
  sandboxConfig: MpSandboxConfigWire | null;
  waitingMessage?: string;
  resultMessage?: string;
  winnerId?: string | null;
  speedrunResults?: MpSpeedrunResultEntry[] | null;
  /** Speedrun FFA: show other players' cursors during the race. */
  speedrunRaceShowCursors?: boolean;
  /** Snake: spawn bouncing balls (+50 every 500 score). */
  snakeBallsEnabled?: boolean;
  soccerTeams?: Record<string, SoccerTeamId>;
  soccerFormat?: SoccerFormat;
  soccerVariant?: SoccerVariantId;
  soccerCompete?: boolean;
  soccerAllowTeamChange?: boolean;
};

export type MpObstacleState = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
};

export type MpHighwayStateWire = {
  cameraY: number;
  obstacles: unknown[];
  nextSpawnWorldY: number;
};

export type MpGameStateSnapshot = {
  tick: number;
  score: number;
  obstacles: MpObstacleState[];
  highway?: MpHighwayStateWire;
  players: Array<{
    id: string;
    x: number;
    y: number;
    alive: boolean;
    isSpectator: boolean;
    trailEnabled?: boolean;
    trailColor?: string;
    speedrunPoints?: number[];
  }>;
  /** Speedrun co-op: union of collected point indices for the whole team. */
  teamSpeedrunPoints?: number[];
  /** Snake: new trail points (norm 0–1) added since the previous broadcast. */
  snakeTrailDeltas?: Array<{ id: string; points: Array<{ x: number; y: number }> }>;
  /** Snake: one-time full trail sync when trails become active (all players). */
  snakeTrailFull?: Array<{ id: string; points: Array<{ x: number; y: number }> }>;
};

export type MpFriendRoomEntry = {
  code: string;
  hostName: string;
  hostAccountId: string;
  playerCount: number;
};

/** Client → server */
export type MpClientMessage =
  | { type: "create_room"; name: string; hostAccountId?: string; equippedCosmeticIds?: string[] }
  | { type: "join_room"; code: string; name: string; equippedCosmeticIds?: string[] }
  | { type: "list_friend_rooms"; friendAccountIds: string[] }
  | { type: "leave_room" }
  | { type: "kick"; targetId: string }
  | { type: "transfer_host"; targetId: string }
  | { type: "select_mode"; mode: MpGameMode }
  | { type: "set_speedrun_race_cursors"; show: boolean }
  | { type: "set_snake_balls"; enabled: boolean }
  | { type: "open_mode_setup" }
  | { type: "select_speedrun_level"; level: number }
  | { type: "confirm_sandbox_config"; config: MpSandboxConfigWire }
  | { type: "cancel_mode_config" }
  | { type: "select_submode"; subMode: MpSubMode }
  | { type: "open_submode_select" }
  | { type: "start_game" }
  | { type: "cancel_submode_select" }
  | { type: "pos"; x: number; y: number; seq: number; trailEnabled?: boolean; trailColor?: string }
  | { type: "return_lobby" }
  | { type: "speedrun_finished"; timeMs: number; device: "pc" | "mobile" }
  | { type: "speedrun_points"; indices: number[] }
  | { type: "speedrun_coop_hit" }
  | { type: "highway_death" }
  | { type: "dismiss_speedrun_results" }
  | { type: "replay_match" }
  | { type: "next_speedrun_level" }
  | { type: "exit_to_lobby" }
  | {
      type: "soccer_queue";
      format: SoccerFormat;
      variant?: SoccerVariantId;
      name: string;
      accountId?: string;
      equippedCosmeticIds?: string[];
      profile?: SoccerProfileWire;
    }
  | { type: "soccer_unqueue" }
  | { type: "soccer_set_team"; team: SoccerTeamId | "random" }
  | { type: "soccer_change_team"; team: SoccerTeamId }
  | { type: "soccer_set_variant"; variant: SoccerVariantId }
  | { type: "soccer_start" }
  | { type: "soccer_boost"; active: boolean }
  | { type: "soccer_rejoin"; playerId: string; token: string; name: string }
  | { type: "soccer_rematch_request" }
  | { type: "soccer_rematch_accept" }
  | { type: "soccer_rematch_decline" }
  | { type: "soccer_leave" }
  | { type: "soccer_skip_replay" };

/** Server → client */
export type MpServerMessage =
  | { type: "connected"; playerId: string }
  | { type: "room_state"; room: MpRoomSnapshot }
  | {
      type: "game_start";
      seed: number;
      mode: MpGameMode;
      subMode: MpSubMode;
      startTime: number;
      speedrunLevel?: number | null;
      sandboxConfig?: MpSandboxConfigWire | null;
      speedrunRaceShowCursors?: boolean;
    }
  | { type: "game_state"; state: MpGameStateSnapshot }
  | { type: "player_died"; playerId: string; coopFail?: boolean }
  | { type: "game_over"; message: string; winnerId?: string | null }
  | { type: "kicked" }
  | { type: "speedrun_results"; results: MpSpeedrunResultEntry[]; level: number }
  | { type: "speedrun_finisher"; id: string; name: string; timeMs: number }
  | { type: "speedrun_coop_respawn"; teamPoints: number[] }
  | { type: "match_to_lobby" }
  | { type: "friend_rooms"; rooms: MpFriendRoomEntry[] }
  | { type: "error"; message: string }
  | { type: "soccer_queue_status"; format: SoccerFormat; variant?: SoccerVariantId; searching: number }
  | { type: "soccer_state"; state: SoccerStateWire; events?: Array<{ kind: string; x: number; y: number; intensity: number; text?: string; playerId?: string }> }
  | { type: "soccer_event"; kind: string; x: number; y: number; intensity: number; text?: string }
  | { type: "soccer_afk_warn"; secondsLeft: number }
  | { type: "soccer_player_net"; playerId: string; status: "reconnecting" | "reconnected" | "forfeit" }
  | { type: "soccer_rematch_update"; accepted: string[]; declined: string[] }
  | { type: "soccer_resume_token"; playerId: string; token: string }
  | { type: "soccer_match_closed" };

export const MP_MIN_PLAYERS = 2;
export const MP_MAX_PLAYERS = 8;
export const MP_ROOM_CODE_LEN = 5;
export const MP_WS_PATH = "/ws/mp";

/** Normalize user-provided or default WebSocket URL. */
export function mpWsUrl(): string {
  const env = (import.meta as { env?: { VITE_MP_WS_URL?: string } }).env?.VITE_MP_WS_URL?.trim();
  let url = env || defaultMpWsUrl();
  if (url.startsWith("https://")) url = `wss://${url.slice(8)}`;
  else if (url.startsWith("http://")) url = `ws://${url.slice(7)}`;
  if (!url.includes("/ws/")) {
    url = `${url.replace(/\/$/, "")}${MP_WS_PATH}`;
  }
  return url;
}

function defaultMpWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${MP_WS_PATH}`;
}

/** Render free tier cold starts can take ~50s; local dev should fail fast. */
export function mpConnectionTimeoutMs(url: string): number {
  if (/localhost|127\.0\.0\.1/.test(url)) return 12_000;
  if (/onrender\.com|railway\.app|fly\.dev/i.test(url)) return 90_000;
  return 25_000;
}

export function mpIsRemoteWsUrl(url: string): boolean {
  return !/localhost|127\.0\.0\.1/.test(url);
}
